import { useQuery } from '@apollo/client';
import { CloudSync } from '@mui/icons-material';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import { object, string } from 'yup';
import { useUpdateDataSource } from '~/api/data-sources';
import { RouterTabs } from '~/components/RouterTabs';
import { LoadingPane } from '~/components/loading-pane';
import { PageContainer } from '~/components/page-layout';
import { InlineEditTitle, Toolbar } from '~/components/toolbar';
import { useAppContext } from '~/contexts';
import { useLink } from '~/hooks/link';
import { DataSourceLayoutDocument } from '../../queries/layout.generated';
import { Actions } from './Actions';

export interface LayoutProps {
  children: ReactNode;
  noPadding?: boolean;
}

export const Layout = ({ children, noPadding = false }: LayoutProps) => {
  const { t } = useTranslation();
  const { currentNetwork } = useAppContext();
  const [update] = useUpdateDataSource();
  const location = useLocation();
  const link = useLink();

  const params = useParams<{ dataSourceId: string }>();
  const dataSourceId = parseInt(params.dataSourceId ?? '');

  const { data, loading } = useQuery(DataSourceLayoutDocument, {
    variables: { networkId: currentNetwork.id, dataSourceId },
  });

  const dataSource = data?.network?.dataSource;

  if (!loading && !dataSource) return <Navigate to={link('/not-found')} replace />;

  return (
    <LoadingPane in={loading && !data} size={80} thickness={4}>
      {dataSource && (
        <>
          <Toolbar
            pageTitle={dataSource.name}
            actions={<Actions dataSource={dataSource} />}
            titleIcon={<CloudSync />}
            titleEditor={
              <InlineEditTitle
                inputId={`data-sources-${dataSource.id}`}
                value={dataSource.name}
                update={(value: string) =>
                  update({ variables: { id: dataSource.id, name: value.trim() } })
                }
                tooltip={t('pages.data_sources.edit.edit_name_tooltip')}
                validationSchema={object({
                  name: string().required(t('common.validation.name_required')).trim(),
                })}
              />
            }
          />
          <nav>
            <RouterTabs
              tabs={[
                { label: t('common.labels.details'), to: '../edit' },
                { label: t('pages.data_sources.tabs.data'), to: '../data' },
                ...(currentNetwork.canManage.value
                  ? [
                      {
                        label: t('common.tabs.audit_logs'),
                        to: { pathname: '../audit-logs', search: location.search },
                      },
                    ]
                  : []),
              ]}
              variant="scrollable"
            />
          </nav>
          <PageContainer padding={!noPadding}>{children}</PageContainer>
        </>
      )}
    </LoadingPane>
  );
};
